import { useEffect } from "react";
import { X } from "@untitledui/icons";
import { FileIcon } from "@untitledui/file-icons";
import { Button } from "@/components/base/buttons/button";
import { cx } from "@/utils/cx";

interface AttachmentPreviewProps {
    files: File[];
    onRemove: (index: number) => void;
    className?: string;
}

const getFileIconType = (fileName: string): string => {
    const extension = fileName.split(".").pop()?.toLowerCase();

    switch (extension) {
        case "pdf":
            return "pdf";
        case "xlsx":
        case "xls":
            return "xlsx";
        case "csv":
            return "csv";
        case "docx":
        case "doc":
            return "docx";
        case "jpg":
        case "jpeg":
        case "png":
        case "gif":
        case "webp":
            return "image";
        case "zip":
            return "zip";
        case "txt":
            return "txt";
        case "ppt":
        case "pptx":
            return "powerpoint";
        default:
            return "generic";
    }
};

const getReadableFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 KB";
    const suffixes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.floor(bytes / Math.pow(1024, i)) + " " + suffixes[i];
};

export const AttachmentPreview = ({ files, onRemove, className }: AttachmentPreviewProps) => {
    // Cleanup object URLs quando il componente si smonta o i file cambiano
    useEffect(() => {
        const imageFiles = files.filter((f) => f.type.startsWith("image/"));
        const urls = imageFiles.map((f) => URL.createObjectURL(f));

        return () => {
            urls.forEach((url) => URL.revokeObjectURL(url));
        };
    }, [files]);

    if (files.length === 0) {
        return null;
    }

    return (
        <div className={cx("flex flex-wrap gap-2 p-3 bg-transparent", className)}>
            {files.map((file, index) => {
                const iconType = getFileIconType(file.name);
                const isImage = file.type.startsWith("image/");
                const previewUrl = isImage ? URL.createObjectURL(file) : null;

                return (
                    <div
                        key={`${file.name}-${index}`}
                        className="relative flex items-center gap-2 rounded-lg border border-secondary bg-primary p-2 max-w-[200px]"
                    >
                        {/* Preview per immagini */}
                        {previewUrl ? (
                            <div className="flex-shrink-0 size-10 rounded overflow-hidden bg-primary">
                                <img
                                    src={previewUrl}
                                    alt={file.name}
                                    className="size-full object-cover"
                                    onLoad={() => {
                                        // Cleanup URL quando l'immagine è caricata
                                        // Il cleanup verrà fatto quando il componente si smonta
                                    }}
                                />
                            </div>
                        ) : (
                            <div className="flex-shrink-0 size-10 rounded flex items-center justify-center bg-primary">
                                <FileIcon type={iconType as any} variant="default" className="size-6" />
                            </div>
                        )}

                        {/* File info */}
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-primary truncate" title={file.name}>
                                {file.name}
                            </div>
                            <div className="text-xs text-tertiary">{getReadableFileSize(file.size)}</div>
                        </div>

                        {/* Remove button */}
                        <Button
                            type="button"
                            onClick={() => onRemove(index)}
                            color="tertiary"
                            size="sm"
                            className="flex-shrink-0"
                            aria-label={`Remove ${file.name}`}
                        >
                            <X className="size-4" />
                        </Button>
                    </div>
                );
            })}
        </div>
    );
};
